import React from 'react';
import { BookOpen } from 'lucide-react';
import styles from './WikiDashboard.module.css';

const WikiAdminGuide = () => (
  <div className={styles.dashboard}>
    <h1 className={styles.title}>
      <BookOpen size={28} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
      Гайд по админке вики
    </h1>

    <div className={styles.section} style={{ marginBottom: '1.5rem' }}>
      <h2 className={styles.sectionTitle}>Как давать названия страницам и категориям</h2>
      <ul style={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, paddingLeft: '1.5rem' }}>
        <li>
          <strong>Slug (URL)</strong> — только латиница (a-z), цифры и дефисы. Например: <code>nazvanie-stranicy</code>. Не используйте пробелы и спецсимволы.
        </li>
        <li>
          <strong>Название</strong> — может быть на русском или любом языке; отображается в меню и в заголовке статьи.
        </li>
        <li>Единый стиль упрощает навигацию: короткие осмысленные slug, понятные названия категорий.</li>
      </ul>
    </div>

    <div className={styles.section} style={{ marginBottom: '1.5rem' }}>
      <h2 className={styles.sectionTitle}>Как пользоваться редактором</h2>
      <ul style={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, paddingLeft: '1.5rem' }}>
        <li><strong>Создание страницы:</strong> «Страницы» → «Создать страницу». Заполните название, slug, выберите категорию и введите контент.</li>
        <li><strong>Черновики:</strong> при редактировании черновик сохраняется в браузере. При повторном открытии можно восстановить несохранённый текст.</li>
        <li><strong>Превью:</strong> кнопка «Превью» показывает, как статья будет выглядеть для читателя.</li>
        <li><strong>Изображения:</strong> вставка из буфера или через кнопку — картинка загружается на сервер и вставляется ссылкой.</li>
        <li><strong>Горячие клавиши:</strong> Escape — закрытие окна (с подтверждением при несохранённых изменениях). В редакторе работают Ctrl+Z / Ctrl+Shift+Z для отмены и повтора.</li>
      </ul>
    </div>

    <div className={styles.section} style={{ marginBottom: '1.5rem' }}>
      <h2 className={styles.sectionTitle}>Что добавлять на страницу</h2>
      <p style={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, marginBottom: '0.75rem' }}>
        Страница вики должна быстро отвечать на вопрос «о чём это» и давать нужную информацию в понятном порядке.
      </p>
      <ul style={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, paddingLeft: '1.5rem' }}>
        <li><strong>Вводный абзац</strong> — в самом начале кратко опишите тему: что это, зачем нужно, кому пригодится. Два–три предложения часто достаточно.</li>
        <li><strong>Ключевые факты в начале</strong> — даты, определения, главный вывод. Читатель, который листает страницу, должен сразу видеть суть.</li>
        <li><strong>Разделы по смыслу</strong> — инструкция (шаги), справочник (описание, параметры), FAQ (вопрос–ответ), описание процесса. Один тип контента на странице или чётко разделённые блоки с подзаголовками.</li>
        <li><strong>Ссылки на связанные страницы</strong> — в конце или в тексте: «Подробнее см. …», «Связанные темы: …». Так вики становится сетью, а не набором изолированных статей.</li>
        <li><strong>Списки и таблицы</strong> — где есть перечисление или сравнение (шаги, параметры, плюсы/минусы), используйте маркированные списки или таблицы вместо длинного текста.</li>
        <li><strong>Изображения</strong> — рядом с тем абзацем, который они иллюстрируют; при необходимости добавьте короткую подпись.</li>
      </ul>
    </div>

    <div className={styles.section} style={{ marginBottom: '1.5rem' }}>
      <h2 className={styles.sectionTitle}>Как структурировать страницу</h2>
      <ul style={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, paddingLeft: '1.5rem' }}>
        <li><strong>Один фокус на страницу.</strong> Одна тема, один процесс или один объект. Если материала много — разбейте на несколько страниц и свяжите их ссылками.</li>
        <li><strong>Порядок разделов</strong> — от общего к частному или по шагам. Сначала «что это», потом «как использовать» или «подробности».</li>
        <li><strong>Заголовки</strong> — не пропускайте уровни: после H2 идёт H3, затем H4. Заголовок должен коротко описывать раздел, без ссылок внутри.</li>
        <li><strong>Не дублируйте большие куски</strong> на других страницах. Лучше одна страница-источник и ссылки на неё («подробнее см. …»).</li>
      </ul>
    </div>

    <div className={styles.section} style={{ marginBottom: '1.5rem' }}>
      <h2 className={styles.sectionTitle}>Именование и навигация</h2>
      <ul style={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, paddingLeft: '1.5rem' }}>
        <li><strong>Название страницы</strong> — понятное и конкретное. Хорошо: «Как настроить интеграцию», «Политика отпусков». Плохо: «Информация», «Разное».</li>
        <li><strong>Slug</strong> — латиница, цифры, дефисы. Отображается в URL. Лучше короткий и читаемый: <code>setup-integration</code>, <code>vacation-policy</code>.</li>
        <li><strong>Категория</strong> — выберите ту, где читатель будет искать эту тему. От этого зависит, в каком разделе вики появится страница.</li>
        <li><strong>Единый стиль названий</strong> в рамках вики упрощает поиск: например, инструкции — «Как сделать X», справочные страницы — «X — описание».</li>
      </ul>
    </div>

    <div className={styles.section} style={{ marginBottom: '1.5rem' }}>
      <h2 className={styles.sectionTitle}>Стиль текста и чего избегать</h2>
      <ul style={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, paddingLeft: '1.5rem' }}>
        <li><strong>Тон</strong> — нейтральный, деловой. Короткие предложения и абзацы. Один термин в вики — одно значение; не меняйте формулировки без причины.</li>
        <li><strong>Избегайте:</strong> длинных абзацев без подзаголовков; дублирования одного и того же в разных разделах; выделения всего подряд (жирным/курсивом); ссылок в заголовках — ссылки в тексте раздела.</li>
        <li><strong>Устаревшее</strong> — если информация устарела, обновите страницу или добавьте пометку и дату («актуально на …», «устарело, см. …»).</li>
      </ul>
    </div>

    <div className={styles.section} style={{ marginBottom: '1.5rem' }}>
      <h2 className={styles.sectionTitle}>Дополнительно</h2>
      <ul style={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, paddingLeft: '1.5rem' }}>
        <li><strong>Массовые действия:</strong> в таблице страниц отметьте чекбоксами нужные страницы — можно удалить выбранные или перенести в другую категорию.</li>
        <li><strong>История:</strong> вкладка «История» — кто и когда менял страницы; фильтры по дате и автору.</li>
        <li><strong>Категории и порядок:</strong> в «Категориях» можно менять порядок перетаскиванием (режим «Изменить порядок»). Подкатегории создаются через «Добавить категорию» с выбором родителя.</li>
        <li><strong>Валидация slug:</strong> при сохранении проверяется формат slug; при ошибке под полем появится подсказка.</li>
      </ul>
    </div>
  </div>
);

export default WikiAdminGuide;
